import { Link } from 'react-router-dom';
import {
  LoadingState,
  StatePanel,
  V2Badge,
  V2Button,
  V2DataGrid,
  V2EmptyState,
  type V2GridColumn,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import {
  amountColumnHeader,
  formatRialFa,
  treasuryTxnStatusLabel,
  type AmountDisplayUnit,
} from '../../lib/finance-display.js';
import {
  SETTLEMENT_METHOD_LABEL,
  type TreasuryTransaction,
  type TreasuryTransactionListResponse,
  type TreasurySettlementMethod,
} from '../../lib/types/finance.js';
import { PartyLabel } from './PartyLabel.js';

interface TxnRow extends TreasuryTransaction {
  readonly id: string;
}

const TXN_STATUS_TONE: Record<string, 'neutral' | 'success' | 'info'> = {
  draft: 'neutral',
  posted: 'success',
  reversed: 'neutral',
};

/**
 * UI_REFERENCE_SOURCE:
 * section: Finance.dc.html — AR «دریافت‌ها» / AP «پرداخت‌ها» sub-tab.
 * status: PARTIAL_REAL — read-only list of treasury receipts (AR) or payments
 *   (AP) from `GET .../finance/treasury/transactions?kind=receipt|payment`
 *   (apps/api/src/finance/treasury.controller.ts `listTransactions`, gated by
 *   treasury.transaction.read — a 403 here is shown as permission_denied and
 *   is independent of the open-item permission). Creation stays on the
 *   treasury page, which owns the receipt/payment + settlement-allocation
 *   form. Not shown: the «تسویه‌ها» (allocation) tab — allocations are only
 *   returned by the single-transaction detail endpoint
 *   (`serializeTreasuryTransaction`), there is no list-settlements endpoint,
 *   and N detail calls per page view would be a fabricated aggregate.
 */
export function OpenItemsTreasuryTab({
  kind,
  slug,
  base,
  unit,
}: {
  readonly kind: 'ar' | 'ap';
  readonly slug: string;
  readonly base: string;
  readonly unit: AmountDisplayUnit;
}) {
  const txnKind = kind === 'ar' ? 'receipt' : 'payment';
  const nouns =
    kind === 'ar'
      ? { plural: 'دریافت‌ها', create: 'ثبت دریافت', empty: 'دریافتی ثبت نشده' }
      : { plural: 'پرداخت‌ها', create: 'ثبت پرداخت', empty: 'پرداختی ثبت نشده' };
  const txns = useApiGet<TreasuryTransactionListResponse>(
    `${base}/api/v1/finance/treasury/transactions?kind=${txnKind}`,
    [slug, kind],
  );

  if (txns.status === 'loading') return <LoadingState />;
  if (txns.status === 'error') {
    return (
      <StatePanel
        variant={txns.error.status === 403 ? 'permission_denied' : 'network_error'}
        title={
          txns.error.status === 403
            ? `دسترسی به ${nouns.plural} خزانه‌داری ندارید`
            : `فهرست ${nouns.plural} قابل نمایش نیست`
        }
      />
    );
  }

  const rows: TxnRow[] = txns.data.items.map((t) => ({ ...t, id: t.treasuryTransactionId }));
  const columns: readonly V2GridColumn<TxnRow>[] = [
    {
      key: 'ref',
      header: 'شمارهٔ مرجع',
      render: (row) => (
        <Link
          className="ltr sk-num"
          to={`${base}/finance/treasury/transactions/${row.treasuryTransactionId}`}
          style={{ fontFamily: 'var(--sk-font-mono)' }}
        >
          {row.sourceReference || '—'}
        </Link>
      ),
    },
    {
      key: 'party',
      header: 'طرف‌حساب',
      render: (row) =>
        row.partyId ? (
          <PartyLabel
            slug={slug}
            partyId={row.partyId}
            href={`${base}/master-data/parties/${row.partyId}`}
          />
        ) : (
          <span style={{ color: 'var(--sk-fg-subtle)' }}>—</span>
        ),
    },
    {
      key: 'date',
      header: 'تاریخ',
      render: (row) => <span className="sk-num">{displayJalaliDate(row.businessDate)}</span>,
    },
    {
      key: 'method',
      header: 'روش',
      render: (row) =>
        row.settlementMethod
          ? (SETTLEMENT_METHOD_LABEL[row.settlementMethod as TreasurySettlementMethod] ?? '—')
          : '—',
    },
    {
      key: 'amount',
      header: amountColumnHeader('مبلغ', unit),
      align: 'end',
      render: (row) => <span className="sk-num">{formatRialFa(row.amountRial, unit)}</span>,
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (row) => (
        <V2Badge tone={TXN_STATUS_TONE[row.status] ?? 'neutral'}>
          {treasuryTxnStatusLabel(row.status)}
        </V2Badge>
      ),
    },
  ];

  return (
    <V2DataGrid
      icon="invoice"
      title={nouns.plural}
      subtitle={`${rows.length.toLocaleString('fa-IR')} مورد · از خزانه‌داری`}
      actions={
        <Link to={`${base}/finance/treasury`}>
          <V2Button variant="primary" size="sm" icon="plus">
            {nouns.create}
          </V2Button>
        </Link>
      }
      columns={columns}
      rows={rows}
      rowKey={(r) => r.id}
      zebra
      emptyState={<V2EmptyState icon="info" title={nouns.empty} />}
    />
  );
}
